/**
 * OutlinerView.tsx - Main Outliner Component
 * 
 * CHANGE HISTORY:
 * - 251123_0005 danielbeer: Implement view-level undo scoping (browse vs edit contexts)
 * - 251120_2048 danielbeer: Added default CSS import for consistent styling
 * - 251115_2235 [pending] danielbeer: Create main Outliner component with full integration
 * 
 * Integrates:
 * - HierarchicalSelectrManager for selection
 * - Commandr for keyboard shortcuts
 * - Backtrackr for undo/redo (with view-level context scoping)
 * - Clippr for clipboard operations
 * 
 * UNDO ARCHITECTURE:
 * - Browse mode: Context = `outliner:view:{viewId}` (structural changes)
 * - Edit mode: Context = `outliner:view:{viewId}:edit:{nodeId}` (text changes per node)
 * - Prevents confusing behavior where undo at browse level switches into edit mode
 * - See docs/UNDO_ARCHITECTURE.md for full details
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import '../styles/outliner.css';
import { HierarchicalSelectrManager, type HierarchicalNode } from '@opcentral/selectr';
import { useCommandr } from '@opcentral/commandr';
import { useBacktrackr } from '@opcentral/backtrackr';
import { useClippr } from '@opcentral/clippr';
import { usePromptr } from '@opcentral/promptr';
import { useOutlinerCommands, useUndoableState } from '../hooks';
import type { OutlinerContext } from '../commands';
import { 
  handleEditKeyDown as handleEditKeyDownUtil,
  handleNavigationKeyDown as handleNavigationKeyDownUtil,
  type KeyboardHandlerContext 
} from '../utils/keyboardHandlers';
import { ContextMenu, type ContextMenuItem } from './ContextMenu';

export interface OutlinerNode extends HierarchicalNode {
  text: string;
  expanded?: boolean;
  children?: string[];
}

export interface OutlinerViewProps {
  nodes: OutlinerNode[];
  onChange?: (nodes: OutlinerNode[]) => void;
  onSelectionChange?: (selectedIds: string[]) => void;
  familyRules?: boolean;
  className?: string;
  viewId?: string; // Unique identifier for this view instance
  renderNodeContent?: (node: OutlinerNode, isEditing: boolean, updateNode: (id: string, updates: Partial<OutlinerNode>) => void) => React.ReactNode; // Custom node content renderer
}

export const OutlinerView: React.FC<OutlinerViewProps> = ({
  nodes: initialNodes,
  onChange,
  onSelectionChange,
  familyRules = true,
  className = '',
  viewId = 'default',
  renderNodeContent
}) => {
  // Load persisted expanded state from localStorage
  const loadExpandedState = useCallback(() => {
    try {
      const key = `outliner:expanded:${viewId}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        return new Set<string>(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('[Outlinr] Failed to load expanded state:', error);
    }
    return new Set<string>();
  }, [viewId]);
  
  // Orchestrators (needed before state)
  const commandr = useCommandr();
  const backtrackr = useBacktrackr();
  const clippr = useClippr();
  const promptr = usePromptr();
  
  // Use fixed browse-level context for undo/redo
  // Text edits during edit mode won't be recorded (handled separately)
  const contextId = `outliner:view:${viewId}`;
  
  // State with automatic undo/redo (only data that should be undoable)
  const [state, setState] = useUndoableState(
    {
      nodes: initialNodes,
      currentNodeId: null as string | null,
      expandedNodes: loadExpandedState(), // Call the function to get the Set
      selectedIds: [] as string[]
    },
    contextId,
    backtrackr
  );
  
  // UI-only state (NOT part of undo - these are ephemeral UI states)
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const [mode, setMode] = useState<'navigation' | 'edit'>('navigation');
  
  // Destructure state for convenience
  const { nodes, currentNodeId, expandedNodes, selectedIds } = state;
  
  // UI-only state (not part of undo)
  const [showFindBox, setShowFindBox] = useState(false);
  const [showFilterBox, setShowFilterBox] = useState(false);
  
  
  // Ref to store snapshot before editing starts
  // Session state for blank item warning (persists for component lifetime)
  const blankItemWarningShownRef = useRef<boolean>(false);
  
  // Track the last created node ID to detect double-Enter on blank nodes
  const lastCreatedNodeIdRef = useRef<string | null>(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null);
  
  // Ref to store snapshot before editing starts
  const editingSnapshotRef = useRef<any>(null);
  
  // Ref for the container div to manage focus
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Ref for the edit input to manage cursor position
  const inputRef = useRef<HTMLInputElement>(null);
  
  // No longer need these - useUndoableState handles it
  
  // Selection manager - create once, update nodes separately
  const selectr = useMemo(() => {
    const manager = new HierarchicalSelectrManager(initialNodes, {
      mode: 'multiple',
      enforceFamilyRules: familyRules,
      persistent: false
    });
    return manager;
  }, [familyRules]); // Only recreate if familyRules changes
  
  // Update selectr when nodes change
  useEffect(() => {
    selectr.updateNodes(nodes);
  }, [nodes, selectr]);
  
  // Initialize with first node selected
  useEffect(() => {
    if (nodes.length > 0 && selectedIds.length === 0) {
      selectr.select(nodes[0].id, 'replace');
    }
  }, []); // Only run once on mount
  
  // useUndoableState handles undo/redo automatically - no manual setup needed!
  
  // Persist expanded state to localStorage
  useEffect(() => {
    try {
      const key = `outliner:expanded:${viewId}`;
      const value = JSON.stringify(Array.from(expandedNodes));
      localStorage.setItem(key, value);
      // console.log('[Outlinr] Persisted expanded state:', expandedNodes.size, 'nodes');
    } catch (error) {
      console.warn('[Outlinr] Failed to persist expanded state:', error);
    }
  }, [expandedNodes, viewId]);
  
  // Listen to selection changes
  useEffect(() => {
    const unsubscribe = selectr.on('change', (event) => {
      const selected = selectr.getSelected();
      setState(
        prev => ({
          ...prev,
          selectedIds: selected,
          currentNodeId: selected.length > 0 ? selected[0] : null
        }),
        'Selection changed (no undo)' // Don't record selection as undoable
      );
      onSelectionChange?.(selected);
    });
    
    return () => unsubscribe();
  }, [selectr, onSelectionChange, setState]);
  
  // Calculate visible nodes (respecting expanded state)
  const visibleNodes = useMemo(() => {
    const visible: string[] = [];
    
    const traverse = (nodeId: string | null, depth: number = 0) => {
      const children = nodes.filter(n => n.parentId === nodeId);
      
      children.forEach(child => {
        visible.push(child.id);
        
        // Only traverse children if expanded
        if (expandedNodes.has(child.id)) {
          traverse(child.id, depth + 1);
        }
      });
    };
    
    traverse(null);
    return visible;
  }, [nodes, expandedNodes]);
  
  // Data operations
  const getData = useCallback(() => nodes, [nodes]);
  
  const updateData = useCallback((newNodes: OutlinerNode[]) => {
    setState(
      prev => {
        // Only update if nodes actually changed
        if (prev.nodes === newNodes) return prev;
        return { ...prev, nodes: newNodes };
      },
      'Update nodes'
    );
  }, [setState]);
  
  // Track if this is the first render to avoid calling onChange on mount
  const isFirstRender = useRef(true);
  const onChangeRef = useRef(onChange);
  
  // Keep ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // Call onChange when nodes change (outside of render)
  useEffect(() => {
    // Skip on first render (initial mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    onChangeRef.current?.(nodes);
  }, [nodes]); // Only depend on nodes, not onChange
  
  const getNode = useCallback((id: string) => {
    return nodes.find(n => n.id === id);
  }, [nodes]);
  
  const addNode = useCallback((node: OutlinerNode, position: 'before' | 'after' | 'child', targetId: string | null) => {
    console.log('[OutlinerView] addNode called:', { node, position, targetId, currentNodesCount: nodes.length });
    setState(prev => {
      const newNodes = [...prev.nodes];
      
      if (targetId === null) {
        // Add to root level
        if (position === 'child') {
          newNodes.push(node);
        } else {
          newNodes.unshift(node);
        }
      } else {
        const targetIndex = newNodes.findIndex(n => n.id === targetId);
        if (targetIndex === -1) return prev;
        
        if (position === 'child') {
          // Add as child - insert after target
          node.parentId = targetId;
          newNodes.splice(targetIndex + 1, 0, node);
        } else {
          // Add as sibling - inherit parent
          const target = newNodes[targetIndex];
          node.parentId = target.parentId;
          const insertIndex = position === 'after' ? targetIndex + 1 : targetIndex;
          newNodes.splice(insertIndex, 0, node);
        }
      }
      
      console.log('[OutlinerView] addNode result:', { newNodesCount: newNodes.length, newNode: node });
      return { ...prev, nodes: newNodes };
    }, `Add node: ${node.text || 'untitled'}`);
  }, [setState, nodes.length]);
  
  const updateNode = useCallback((id: string, updates: Partial<OutlinerNode>, recordUndo: boolean = true) => {
    if (recordUndo) {
      setState(prev => {
        const newNodes = prev.nodes.map(n => 
          n.id === id ? { ...n, ...updates } : n
        );
        return { ...prev, nodes: newNodes };
      }, `Update node: ${id}`);
    } else {
      // No undo recording - just update directly
      setState(prev => ({
        ...prev,
        nodes: prev.nodes.map(n => n.id === id ? { ...n, ...updates } : n)
      }), 'Update node (no undo)');
    }
  }, [setState]);
  
  const deleteNodes = useCallback((ids: string[]) => {
    console.log('[deleteNodes] Called with IDs:', ids);
    setState(prev => {
      const idsToDelete = new Set(ids);
      
      // Also delete all descendants
      const getAllDescendants = (nodeId: string): string[] => {
        const children = prev.nodes.filter(n => n.parentId === nodeId);
        const descendants = children.map(c => c.id);
        children.forEach(c => {
          descendants.push(...getAllDescendants(c.id));
        });
        return descendants;
      };
      
      ids.forEach(id => {
        getAllDescendants(id).forEach(descId => idsToDelete.add(descId));
      });
      
      console.log('[deleteNodes] Total IDs to delete (including descendants):', Array.from(idsToDelete));
      const newNodes = prev.nodes.filter(n => !idsToDelete.has(n.id));
      console.log('[deleteNodes] Nodes before:', prev.nodes.length, 'after:', newNodes.length);
      
      return {
        ...prev,
        nodes: newNodes,
        selectedIds: prev.selectedIds.filter(id => !idsToDelete.has(id))
      };
    }, `Delete ${ids.length} node(s)`);
  }, [setState]);
  
  const moveNode = useCallback((id: string, targetId: string, position: 'before' | 'after' | 'child') => {
    // TODO: Implement with family validation
    // console.log('Move node:', id, 'to', targetId, position);
  }, []);
  
  // Helper to show combine dialog and handle combine
  const showCombineDialogFor = useCallback(async (nodeIds: string[], direction: 'up' | 'down', type: 'move' | 'generation') => {
    // Find the oldest (highest in tree) item
    const sortedByGeneration = nodeIds
      .map(id => nodes.find(n => n.id === id))
      .filter(Boolean)
      .sort((a, b) => (a!.generation || 0) - (b!.generation || 0));
    
    const oldestItem = sortedByGeneration[0];
    if (!oldestItem) return;
    
    const targetParentId = oldestItem.parentId;
    const oldestItemId = oldestItem.id;
    
    // Show confirm dialog using promptr
    const actionText = type === 'generation' && direction === 'down' ? 'indent' :
                      type === 'generation' && direction === 'up' ? 'outdent' :
                      type === 'move' && direction === 'up' ? 'move up' :
                      type === 'move' && direction === 'down' ? 'move down' :
                      'move';
    
    const confirmed = await promptr.confirm({
      title: 'Combine Generations',
      message: `The selected items are in different generations (different parent levels).

To ${actionText} them together, they need to be combined into one generation first.

All selected items will be moved to become siblings of the oldest (highest) selected item.`,
      confirmText: 'Combine & Continue',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) {
      // User cancelled - refocus container
      setTimeout(() => containerRef.current?.focus(), 100);
      return;
    }
    
    // User confirmed - perform combine
    setState(prev => {
      // Get target siblings to find insertion point
      const targetSiblings = prev.nodes.filter(n => n.parentId === targetParentId);
      const oldestItemIndex = targetSiblings.findIndex(n => n.id === oldestItemId);
      
      // Move all other selected items to be siblings of oldest, after it
      const newNodes = prev.nodes.map(n => {
        if (nodeIds.includes(n.id) && n.id !== oldestItemId) {
          return { ...n, parentId: targetParentId, generation: oldestItem.generation };
        }
        return n;
      });
      
      return { ...prev, nodes: newNodes };
    }, `Combined ${nodeIds.length} items into one generation`);
    
    // Refocus container
    setTimeout(() => containerRef.current?.focus(), 100);
  }, [nodes, promptr, setState]);
  
  // View operations
  const expandNode = useCallback((id: string) => {
    setState(
      prev => ({
        ...prev,
        expandedNodes: new Set([...prev.expandedNodes, id])
      }),
      `Expand node: ${id}`
    );
  }, [setState]);
  
  const collapseNode = useCallback((id: string) => {
    setState(prev => {
      const next = new Set(prev.expandedNodes);
      next.delete(id);
      return { ...prev, expandedNodes: next };
    }, `Collapse node: ${id}`);
  }, [setState]);
  
  const isExpanded = useCallback((id: string) => {
    return expandedNodes.has(id);
  }, [expandedNodes]);
  
  // Edit operations (don't record these as undoable - they're just UI state)
  const startEdit = useCallback((id: string) => {
    // Update UI state (not part of undo)
    setEditingNodeId(id);
    setMode('edit');
  }, []);
  
  const exitEdit = useCallback(() => {
    // Update UI state (not part of undo)
    setEditingNodeId(null);
    setMode('navigation');
  }, []);
  
  const cancelEdit = useCallback(() => {
    exitEdit();
  }, [exitEdit]);
  
  const commitEdit = useCallback(async (options?: { deleteIfBlank?: boolean }) => {
    if (!editingNodeId) return;
    
    const node = getNode(editingNodeId);
    if (!node) return;
    
    // Check if node is empty or blank
    const isBlank = !node.text || node.text.trim() === '';
    
    if (isBlank) {
      // If this is a double-Enter on a just-created node, delete it
      if (options?.deleteIfBlank && editingNodeId === lastCreatedNodeIdRef.current) {
        // Find the previous node to select
        const currentIndex = visibleNodes.indexOf(editingNodeId);
        let nextSelection: string | null = null;
        
        if (currentIndex > 0) {
          nextSelection = visibleNodes[currentIndex - 1];
        } else if (visibleNodes.length > 1) {
          nextSelection = visibleNodes[1];
        }
        
        // Delete the blank node
        deleteNodes([editingNodeId]);
        
        // Select the previous node
        if (nextSelection) {
          selectr.select(nextSelection, 'replace');
        }
        
        // Clear the last created node ref
        lastCreatedNodeIdRef.current = null;
        
        setState(
          prev => ({ ...prev, editingNodeId: null, mode: 'navigation' }),
          'Exit edit mode after delete'
        );
        return;
      }
      
      // First time creating blank item this session - show warning
      if (!blankItemWarningShownRef.current) {
        const confirmed = await promptr.confirm({
          title: 'Blank Item Warning',
          message: 'Blank items are not recommended as they can make your outline harder to navigate. Are you sure you want to create a blank item?',
          confirmText: 'Yes, Create Blank',
          cancelText: 'Cancel',
          type: 'warning'
        });
        
        // Mark warning as shown for this session
        blankItemWarningShownRef.current = true;
        
        if (!confirmed) {
          // User cancelled - stay in edit mode
          return;
        }
      }
      
      // User confirmed or already confirmed this session - allow blank item
      // Clear the last created node ref since user confirmed keeping it blank
      lastCreatedNodeIdRef.current = null;
    } else {
      // Non-blank node - clear the last created node ref
      lastCreatedNodeIdRef.current = null;
    }
    
    // Exit edit mode (calls exitEdit which handles context switching)
    exitEdit();
  }, [editingNodeId, getNode, visibleNodes, selectr, promptr, deleteNodes, exitEdit]);
  
  // Create keyboard handler context
  const keyboardContext: KeyboardHandlerContext = useMemo(() => ({
    nodes,
    visibleNodes,
    editingNodeId,
    currentNodeId,
    selectr,
    startEdit,
    exitEdit,
    updateNode,
    updateData,
    deleteNodes,
    addNode,
    expandNode,
    collapseNode,
    isExpanded,
    clippr,
    promptr,
    containerRef,
    inputRef
  }), [
    nodes,
    visibleNodes,
    editingNodeId,
    currentNodeId,
    selectr,
    startEdit,
    exitEdit,
    updateNode,
    updateData,
    deleteNodes,
    addNode,
    expandNode,
    collapseNode,
    isExpanded,
    clippr,
    promptr
  ]);
  
  // Keyboard handler wrappers
  const handleEditKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, nodeId: string) => {
    handleEditKeyDownUtil(e, nodeId, keyboardContext);
  }, [keyboardContext]);
  
  const handleNavigationKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    handleNavigationKeyDownUtil(e, keyboardContext);
  }, [keyboardContext]);
  
  // Create context
  const context: OutlinerContext = useMemo(() => ({
    selectr,
    currentNodeId,
    visibleNodes,
    getData,
    updateData,
    getNode,
    addNode,
    updateNode,
    deleteNodes,
    moveNode,
    expandNode,
    collapseNode,
    isExpanded,
    startEdit,
    cancelEdit,
    isEditing: editingNodeId !== null,
    mode,
    clippr,
    backtrackr,
    showFind: () => setShowFindBox(true),
    showFilter: () => setShowFilterBox(true),
    showCombineDialog: showCombineDialogFor
  }), [
    selectr,
    currentNodeId,
    visibleNodes,
    getData,
    updateData,
    getNode,
    addNode,
    updateNode,
    deleteNodes,
    moveNode,
    expandNode,
    collapseNode,
    isExpanded,
    startEdit,
    cancelEdit,
    editingNodeId,
    mode,
    clippr,
    backtrackr,
    showCombineDialogFor
  ]);
  
  // Register commands
  const commandRegistry = useOutlinerCommands(context, true);
  
  // Get command handlers for direct execution (for context menu)
  const commandHandlers = useMemo(() => {
    const handlers = (commandRegistry as any)?.handlers;
    return handlers || {};
  }, [commandRegistry]);
  
  // Type-to-edit: Any printable character starts editing
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Only in navigation mode
      if (mode !== 'navigation') return;
      
      // Only if a node is selected
      if (!currentNodeId) return;
      
      // Ignore if modifier keys (except Shift for capitals)
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      
      // Only single printable characters
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        
        // Start editing with the typed character
        startEdit(currentNodeId);
        
        // Set the initial value after render
        setTimeout(() => {
          const input = document.querySelector(`input[data-node-id="${currentNodeId}"]`) as HTMLInputElement;
          if (input) {
            input.value = e.key;
            input.setSelectionRange(1, 1);
          }
        }, 0);
      }
    };
    
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [mode, currentNodeId, startEdit]);
  
  // Render node
  const renderNode = (nodeId: string, depth: number = 0) => {
    const node = getNode(nodeId);
    if (!node) return null;
    
    const isSelected = selectedIds.includes(nodeId); // Use state instead of selectr
    const isEditing = editingNodeId === nodeId;
    const hasChildren = nodes.some(n => n.parentId === nodeId);
    const expanded = isExpanded(nodeId);
    
    return (
      <div
        key={nodeId}
        className={`outliner-node ${isSelected ? 'selected' : ''}`}
        style={{ paddingLeft: `${depth * 20}px` }}
        onClick={(e) => {
          e.stopPropagation();
          
          // Determine selection mode based on modifiers
          let mode: 'replace' | 'add' | 'range' = 'replace';
          
          if (e.shiftKey) {
            // Shift+Click: Range selection
            mode = 'range';
            const anchor = selectr.getAnchor();
            if (anchor) {
              // Calculate range manually to preserve anchor
              const anchorIndex = visibleNodes.indexOf(anchor);
              const clickedIndex = visibleNodes.indexOf(nodeId);
              
              if (anchorIndex !== -1 && clickedIndex !== -1) {
                const start = Math.min(anchorIndex, clickedIndex);
                const end = Math.max(anchorIndex, clickedIndex);
                const rangeIds = visibleNodes.slice(start, end + 1);
                
                // Manually update selection to preserve anchor
                (selectr as any).selected.clear();
                rangeIds.forEach(id => (selectr as any).selected.add(id));
                (selectr as any).anchorId = anchor;
                (selectr as any).emit('change', rangeIds);
              }
            } else {
              selectr.select(nodeId, 'replace');
            }
          } else if (e.metaKey || e.ctrlKey) {
            // Cmd/Ctrl+Click: Toggle selection
            mode = 'add';
            if (selectr.isSelected(nodeId)) {
              selectr.deselect(nodeId);
            } else {
              selectr.select(nodeId, 'add');
            }
          } else {
            // Normal click: Replace selection
            selectr.select(nodeId, 'replace');
          }
          
          // console.log('Node clicked:', nodeId, 'mode:', mode, 'selected:', selectr.getSelected());
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          // Double-click to enter edit mode (like Space key)
          startEdit(nodeId);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // If right-clicked node is not selected, select it first
          if (!isSelected) {
            selectr.select(nodeId, 'replace');
          }
          
          // Show context menu
          setContextMenu({ x: e.clientX, y: e.clientY, nodeId });
        }}
      >
        <button
          className="expand-button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) {
              expanded ? collapseNode(nodeId) : expandNode(nodeId);
            }
          }}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {hasChildren && (expanded ? '▼' : '▶')}
        </button>
        
        {renderNodeContent ? (
          renderNodeContent(node, isEditing, updateNode)
        ) : isEditing ? (
          <input
            ref={inputRef}
            type="text"
            data-node-id={nodeId}
            value={node.text}
            onChange={(e) => updateNode(nodeId, { text: e.target.value }, false)}
            onBlur={() => cancelEdit()}
            onKeyDown={(e) => {
              handleEditKeyDown(e, nodeId);
              // Stop propagation to prevent container's navigation handler from receiving the event
              e.stopPropagation();
            }}
            autoFocus
          />
        ) : (
          <span className="node-text">{node.text}</span>
        )}
      </div>
    );
  };
  
  // Render tree
  const renderTree = () => {
    // Show empty state if no nodes
    if (visibleNodes.length === 0) {
      return (
        <div style={{
          padding: '40px 20px',
          textAlign: 'center',
          color: '#999',
          fontStyle: 'italic'
        }}>
          Press <kbd style={{
            padding: '2px 6px',
            backgroundColor: '#f0f0f0',
            border: '1px solid #ccc',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontStyle: 'normal'
          }}>Enter</kbd> to create your first item
        </div>
      );
    }
    
    return visibleNodes.map(nodeId => {
      const node = getNode(nodeId);
      if (!node) return null;
      return renderNode(nodeId, node.generation);
    });
  };
  
  // Auto-focus container on mount so keyboard shortcuts work immediately
  useEffect(() => {
    containerRef.current?.focus();
  }, []);
  
  // Handle click on container to focus
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // Close context menu on any click
    setContextMenu(null);
    
    // Only focus if clicking on the container itself (not on nodes or inputs)
    if (e.target === containerRef.current || (e.target as HTMLElement).classList.contains('outliner-view')) {
      containerRef.current?.focus();
    }
  }, []);
  
  return (
    <div 
      ref={containerRef}
      className={`outliner-view ${className}`}
      tabIndex={0}
      style={{ outline: 'none' }}
      onClick={handleContainerClick}
      onKeyDown={handleNavigationKeyDown}
    >
      {/* Find Box */}
      {showFindBox && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          <input
            type="text"
            placeholder="Find..."
            autoFocus
            style={{
              border: '1px solid #ddd',
              borderRadius: '3px',
              padding: '4px 8px',
              marginRight: '8px'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowFindBox(false);
              }
            }}
          />
          <button onClick={() => setShowFindBox(false)}>✕</button>
        </div>
      )}
      
      {/* Filter Box */}
      {showFilterBox && (
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000
        }}>
          <input
            type="text"
            placeholder="Filter..."
            autoFocus
            style={{
              border: '1px solid #ddd',
              borderRadius: '3px',
              padding: '4px 8px',
              marginRight: '8px'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setShowFilterBox(false);
              }
            }}
          />
          <button onClick={() => setShowFilterBox(false)}>✕</button>
        </div>
      )}
      
      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={[
            { id: 'cut', label: 'Cut', command: 'outliner:cut', shortcut: '⌘X' },
            { 
              id: 'copy', 
              label: 'Copy As', 
              command: 'outliner:copy',
              shortcut: '⌘C',
              submenu: [
                { id: 'copy-tab', label: 'Tab Indented (Default)', command: 'outliner:copy-tab-indented' },
                { id: 'copy-space', label: 'Space Indented', command: 'outliner:copy-space-indented' },
                { id: 'copy-markdown', label: 'Markdown List', command: 'outliner:copy-markdown' },
                { id: 'copy-numbered', label: 'Numbered List', command: 'outliner:copy-numbered' },
                { id: 'copy-outline', label: 'Bullet Outline', command: 'outliner:copy-outline' },
                { id: 'copy-json', label: 'JSON', command: 'outliner:copy-json' }
              ]
            },
            { id: 'paste', label: 'Paste', command: 'outliner:paste', shortcut: '⌘V' },
            { id: 'div1', label: '', command: '', divider: true },
            { id: 'indent', label: 'Indent', command: 'outliner:indent', shortcut: '⇥' },
            { id: 'outdent', label: 'Outdent', command: 'outliner:outdent', shortcut: '⇧⇥' },
            { id: 'div2', label: '', command: '', divider: true },
            { id: 'move-up', label: 'Move Up', command: 'outliner:move-up', shortcut: '⌘↑' },
            { id: 'move-down', label: 'Move Down', command: 'outliner:move-down', shortcut: '⌘↓' },
            { id: 'div3', label: '', command: '', divider: true },
            { id: 'delete', label: 'Delete', command: 'outliner:delete', shortcut: '⌫', danger: true }
          ]}
          onSelect={(command) => {
            // Map command IDs to handler names
            const handlerMap: Record<string, string> = {
              'outliner:cut': 'handleCut',
              'outliner:copy-tab-indented': 'handleCopyAsTabIndented',
              'outliner:copy-space-indented': 'handleCopyAsSpaceIndented',
              'outliner:copy-markdown': 'handleCopyAsMarkdown',
              'outliner:copy-numbered': 'handleCopyAsNumbered',
              'outliner:copy-outline': 'handleCopyAsOutline',
              'outliner:copy-json': 'handleCopyAsJson',
              'outliner:paste': 'handlePaste',
              'outliner:indent': 'handleIndent',
              'outliner:outdent': 'handleOutdent',
              'outliner:move-up': 'handleMoveUp',
              'outliner:move-down': 'handleMoveDown',
              'outliner:delete': 'handleDelete'
            };
            
            const handlerName = handlerMap[command];
            if (handlerName && commandHandlers[handlerName]) {
              commandHandlers[handlerName]();
            } else {
              console.warn('[OutlinerView] No handler found for command:', command);
            }
          }}
          onClose={() => setContextMenu(null)}
        />
      )}
      
      {renderTree()}
    </div>
  );
};
